import React, { Fragment, useContext, useEffect, useState } from 'react';
import { FormCheckbox, Input, RadioGroup, ThemePrepared } from '@fluentui/react-northstar';
import Filters from './Filters';
import { CustomViewContext } from '../CustomViewContextProvider';
import { ColumnsView } from './ColumnsView/ColumnsView';
import ToolbarCSS from '../Toolbar.module.css';
import { useTranslation } from 'react-i18next';
import { ScopePermissionPicker } from '../../../Rbac/ScopePermissionPicker';
import { ScopingDetailsData } from '../../../shared/components/Wysiwyg/HelpCardAddButton';
import { appState } from '../../../AppState';
import { CustomPropertiesAnswerJsonKey, ADMINISTRATORS, ANALYSTS, CustomViewType, LICENSE_FEATURE } from '../../../shared/utils/constants';
import { CheckFeature } from '../../../shared/components/CheckFeature';

interface Props {
  defaultFilters: FilterPropsItems[];
  selectedRequesters: PeoplePickerUserType[];
  setSelectedRequesters: React.Dispatch<React.SetStateAction<PeoplePickerUserType[]>>;
  globalTheme: ThemePrepared<any>;
  isIdOrRequestSelected: boolean;
  invalidRequesterMsg: string;
  setInvalidRequesterMsg: React.Dispatch<React.SetStateAction<string>>;
  selectedAffectedUsers: PeoplePickerUserType[];
  setSelectedAffectedUsers: React.Dispatch<React.SetStateAction<PeoplePickerUserType[]>>;
  invalidAffectedUserMsg: string;
  setInvalidAffectedUserMsg: React.Dispatch<React.SetStateAction<string>>;
  listFeatureStates: ListFeatureStates;
  setScopedData: React.Dispatch<React.SetStateAction<ScopingDetailsData>>;
  scopedData: ScopingDetailsData;
}

const Content = ({
  defaultFilters,
  selectedRequesters,
  setSelectedRequesters,
  globalTheme,
  isIdOrRequestSelected,
  invalidRequesterMsg,
  setInvalidRequesterMsg,
  selectedAffectedUsers,
  setSelectedAffectedUsers,
  invalidAffectedUserMsg,
  setInvalidAffectedUserMsg,
  listFeatureStates,
  setScopedData,
  scopedData
}: Props) => {
  const appContextState = appState();
  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);
  const { view, viewName } = customViewStates;
  const [isDefault, setIsDefault] = useState(false);
  const isAdmin = appContextState.userRoles.roles.includes(ADMINISTRATORS);
  const isAnalyst = appContextState.userRoles.roles.includes(ANALYSTS);
  
  const {t} = useTranslation();

  // Resolved on the ticket list with every other licence flag, NOT fetched here. ColumnsView
  // mutates selectedColumns during its first render, dropping any selected column this
  // predicate rejects - so a flag that is still false while a promise settles silently
  // deletes a saved Total Agent Time column from the user's view.
  const filtersByFeature = (item: any, field: string) => {
    if(!listFeatureStates.useTeam && item[field] == "Team") return false; 
    if(!listFeatureStates.useTicketType && (item[field] == "Type" || item[field] == "Ticket Type")) return false; 
    if(!listFeatureStates.useTicketLifecycle && (item[field] == "Lifecycle" || item[field] == "Phase")) return false; 
    if(!listFeatureStates.useSLA && (item[field] == "SLAs")) return false;
    const resNoteLabels = [t('resolution-note.resolution-category'), t('resolution-note.resolution-note'), t('resolution-note.resolution-note-internal'), t('resolution-note.draft-kb')];
    if(!listFeatureStates.userResolutionNote && (resNoteLabels.some(x => x == item[field]))) return false;
    const groupChatLabel = t('ticket.ticket-list.group-chat');
    if(!listFeatureStates.useGroupChat && item[field] == groupChatLabel) return false;
    if(!listFeatureStates.useCustomProperties && item[field] == CustomPropertiesAnswerJsonKey) return false;
    if(!listFeatureStates.useTimeTracking && item[field] == t('ticket.ticket-list.total-agent-time')) return false;

    return true;
  };

  const featureRestrictFilters = defaultFilters.filter(item => filtersByFeature(item, "title"));

  useEffect(() => {
    setInvalidRequesterMsg('')
    setIsDefault(view!="add" && customViewStates.selectedCustomView.Type==CustomViewType.DefaultView)
    setScopedData({ScopedMembers: [], Type: CustomViewType.MyView});
  }, [])

  const getItems = () => {
    return [
      {
        name: CustomViewType.MyView,
        key: CustomViewType.MyView,
        label: t('ticket.ticket-list.configure-column.custom-view'),
        value: CustomViewType.MyView,
        disabled:isDefault
      },
      {
        name: CustomViewType.ScopedView,
        key: CustomViewType.ScopedView,
        label: t('ticket.ticket-list.configure-column.scoped-view'),
        value: CustomViewType.ScopedView,
        disabled:isDefault
      },
      {
        name: CustomViewType.CompanyView,
        key: CustomViewType.CompanyView,
        label: t('ticket.ticket-list.configure-column.organizational-view'),
        value: CustomViewType.CompanyView,
        disabled:isDefault
      },
    ]
  }

  return (
    <Fragment>
      {view !== 'columns' && (
        <>
          <div className='font-semibold mt-2'>{t('ticket.ticket-list.configure-column.name')}</div>
          <Input
            fluid
            onChange={(e, p) => { setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, viewName: p.value })) }}
            value={viewName}
            inverted
            placeholder={t('ticket.ticket-list.configure-column.enter-view-name')}
            className={`mt-2 ${ToolbarCSS['custom-view-name']}`}
          />

          {(isAdmin || isAnalyst) && 
            <>
              <CheckFeature featureName={LICENSE_FEATURE.PermissionScoping}>
                <div className={`${ToolbarCSS.ViewTypeContainer} py-4`}>
                  <RadioGroup
                    defaultCheckedValue={scopedData.Type==CustomViewType.DefaultView ? CustomViewType.CompanyView : scopedData.Type}
                    items={getItems()}
                    onCheckedValueChange={(e, props) =>{
                      setScopedData(preState => ({ ScopedMembers: [], Type: (props.value.toString()==CustomViewType.CompanyView && isDefault) ? CustomViewType.DefaultView : props.value.toString()}));
                    }}
                  />

                  {(isAdmin && (scopedData.Type==CustomViewType.CompanyView || scopedData.Type==CustomViewType.DefaultView)) &&
                    <FormCheckbox
                      className={ToolbarCSS.ViewTypeCK}
                      label={t('ticket.ticket-list.configure-column.default-view')}
                      disabled={isDefault}
                      checked={isDefault || scopedData.Type==CustomViewType.DefaultView}
                      onChange={(_, checked) => {
                        setScopedData(preState => ({ScopedMembers: [], Type: checked.checked ? CustomViewType.DefaultView : CustomViewType.CompanyView}));
                      }}
                    />
                  }
                </div>
                {scopedData.Type==CustomViewType.ScopedView &&
                  <>
                    <CheckFeature featureName={LICENSE_FEATURE.PermissionScoping}>
                      <div>
                        <ScopePermissionPicker
                          showEnableCheckbox={false}
                          enabled={true}
                          data={scopedData}
                          description={t('scoping.description-view')}
                          changed={(members,isEnabled)=>{
                            setScopedData({ScopedMembers: members, Type: CustomViewType.ScopedView});
                          }}
                        />
                      </div>
                    </CheckFeature>

                  </>
                  
                }
              </CheckFeature>
              
            </>
          }
          

          <Filters
            {...{
              defaultFilters: featureRestrictFilters,
              selectedRequesters,
              setSelectedRequesters,
              invalidRequesterMsg,
              setInvalidRequesterMsg,
              selectedAffectedUsers,
              setSelectedAffectedUsers,
              invalidAffectedUserMsg,
              setInvalidAffectedUserMsg,
              filtersByFeature
            }}
          />
        </>
      )}
      <ColumnsView
        globalTheme={globalTheme}
        isIdOrRequestSelected={isIdOrRequestSelected}
        filtersByFeature={filtersByFeature}
      />
    </Fragment>
  );
};

export default Content;
